/* This example requires Tailwind CSS v2.0+ */

import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import { HAS_FREE_PLAN } from '@/constants/common';
import { Preset } from '@/constants/presets';

const steps = [
  {
    name: 'Create a free account',
    description: 'Vitae sed mi luctus laoreet.',
    href: '#',
    status: 'complete',
  },
  {
    name: "Once you've logged in, find the Poem Title tool amongst our 100+ tools.",
    description: 'Cursus semper viverra facilisis et et some more.',
    href: '#',
    status: 'current',
  },
  {
    name: 'Business information',
    description: 'Penatibus eu quis ante.',
    href: '#',
    status: 'upcoming',
  },
  {
    name: 'Theme',
    description: 'Faucibus nec enim leo et.',
    href: '#',
    status: 'upcoming',
  },
  {
    name: 'Preview',
    description: 'Iusto et officia maiores porro ad non quas.',
    href: '#',
    status: 'upcoming',
  },
];

export default function Steps({ preset }: { preset: Preset }) {
  const { t, lang } = useLocale('steps');

  return (
    <nav aria-label='Progress'>
      <ol role='list' className='max-w-xl overflow-hidden'>
        <li className={clsxm('pb-10', 'relative')}>
          <div
            className='absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-indigo-600'
            aria-hidden='true'
          />

          <span className='group relative flex items-center'>
            <span className='flex h-9 items-center'>
              <span className='relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 font-bold text-white group-hover:bg-indigo-800'>
                1
              </span>
            </span>
            <span className='ml-4 flex min-w-0 flex-col'>
              <span className='text-md font-medium dark:text-slate-400'>
                {t('Create a free account')}
              </span>
            </span>
          </span>
        </li>
        <li className={clsxm('pb-10', 'relative')}>
          <div
            className='absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-indigo-600'
            aria-hidden='true'
          />

          <span className='group relative flex items-center'>
            <span className='flex h-9 items-center'>
              <span className='relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 font-bold text-white group-hover:bg-indigo-800'>
                2
              </span>
            </span>
            <span className='ml-4 flex min-w-0 flex-col'>
              <span className='text-md font-medium dark:text-slate-400'>
                {lang === 'en' ? (
                  <>
                    Once you've logged in, find the <b>{preset.title}</b>{' '}
                    template amongst our 200+ templates.
                  </>
                ) : (
                  t(
                    'Once you’ve logged in, find the required tool amongst our 100+ tools.',
                  )
                )}
              </span>
            </span>
          </span>
        </li>
        {lang === 'en' && (
          <>
            {preset?.fields?.map((step, stepIdx) => (
              <li
                key={step.name}
                className={clsxm(
                  stepIdx !== steps.length - 1 ? 'pb-10' : '',
                  'relative',
                )}
              >
                {stepIdx !== steps.length && (
                  <div
                    className='absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-indigo-600'
                    aria-hidden='true'
                  />
                )}

                <span className='group relative flex items-center'>
                  <span className='flex h-9 items-center'>
                    <span className='relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 font-bold text-white group-hover:bg-indigo-800'>
                      {stepIdx + 3}
                    </span>
                  </span>
                  <span className='ml-4 flex min-w-0 flex-col'>
                    <span className='text-md font-medium dark:text-slate-400'>
                      {(() => {
                        if (step.name === 'length') {
                          return `Select output length: ${step.radioOptions?.map((option) => option.name).join(', ') || 'available options'}`;
                        }
                        if (step.type === 'select') {
                          return `Select ${step.label} from: ${step.options?.join(', ') || 'available options'}`;
                        }
                        return `Fill out ${step.label}. For ${step.placeholder}`;
                      })()}
                    </span>
                  </span>
                </span>
              </li>
            ))}
          </>
        )}
        {lang !== 'en' && (
          <li className={clsxm('pb-10', 'relative')}>
            <div
              className='absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-indigo-600'
              aria-hidden='true'
            />
            <span className='group relative flex items-center'>
              <span className='flex h-9 items-center'>
                <span className='relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 font-bold text-white group-hover:bg-indigo-800'>
                  3
                </span>
              </span>
              <span className='ml-4 flex min-w-0 flex-col'>
                <span className='text-md font-medium dark:text-slate-400'>
                  {t(
                    'Fill out the required fields. For example, if you want to generate a poem, fill out the required fields for the Poem Title tool.',
                  )}
                </span>
              </span>
            </span>
          </li>
        )}
        <li className={clsxm('pb-10', 'relative')}>
          <div
            className='absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-indigo-600'
            aria-hidden='true'
          />
          <span className='group relative flex items-center'>
            <span className='flex h-9 items-center'>
              <span className='relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 font-bold text-white group-hover:bg-indigo-800'>
                {Number(preset?.fields?.length) + (lang === 'en' ? 3 : 2)}
              </span>
            </span>
            <span className='ml-4 flex min-w-0 flex-col'>
              <span className='text-md font-medium dark:text-slate-400'>
                {t(
                  "Enable the 'Use advanced AI model?' toggle to leverage our cutting-edge AI technology for superior performance and more accurate results! Powered by GPT-4.",
                )}
              </span>
            </span>
          </span>
        </li>
        <li className={clsxm('pb-10', 'relative')}>
          <span className='group relative flex items-center'>
            <span className='flex h-9 items-center'>
              <span className='relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 font-bold text-white group-hover:bg-indigo-800'>
                {Number(preset?.fields?.length) + (lang === 'en' ? 4 : 3)}
              </span>
            </span>
            <span className='ml-4 flex min-w-0 flex-col'>
              <span className='text-md font-medium dark:text-slate-400'>
                {lang === 'en' ? (
                  <>
                    Click on <b>Generate</b> and get tons of ideas, generated by
                    A.I. 🤖
                  </>
                ) : (
                  t(
                    'Click on Generate and get tons of ideas, generated by A.I.',
                  )
                )}
              </span>
            </span>
          </span>
        </li>
      </ol>
      <div className='mb-16 sm:flex sm:justify-center'>
        <div className='rounded-md shadow'>
          <Link
            href={
              HAS_FREE_PLAN
                ? `/login?preset=${encodeURIComponent(preset.loginPreset || preset.slug)}`
                : '/#pricing'
            }
            className='flex w-full items-center justify-center rounded-md border border-transparent bg-primary-600 px-8 py-3 text-base font-medium text-white hover:bg-primary-700  md:px-10 md:py-4 md:text-lg'
          >
            {preset.onlyForPaidPlans
              ? t('Get started')
              : t('Try it yourself for free')}
          </Link>
        </div>
      </div>
    </nav>
  );
}
